import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, Col, Row, Space, Tag, Tooltip, Typography, App } from 'antd'
import { LockOutlined, InfoCircleOutlined, MailOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import { format } from 'date-fns'

import IntlMessages from '../../util/IntlMessages'
import UpdateWorkweekForm from '../../components/update-workweek-form'
import UserUpdateForm from '../../components/user-update-form'
import UserLeaveQuotas from '@vacationtracker/shared/components/user-leave-quotas'
import { useAppSelector } from '../../store/hooks'
import { selectAuthUserSlice } from '../../store/auth-user-slice'
import { useLocationAdmin } from '../../hooks/use-location-admin'

import { IUserProfileTab } from './types'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { getOnboardingLink } from '../../util/get-onboarding-link'
import { selectFeatureFlagsSlice } from '../../store/feature-flags-slice'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import { IWorkingSchedule, WorkingHours } from '@vacationtracker/shared/types/work-week'
import UserWorkingDaysAndHours from './working-days-and-hours'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'

const { Paragraph, Text } = Typography

const UserProfileTab = ({
  user,
  amIAdmin,
  isLocationAdmin,
  workingHours,
  firstDayOfWeek,
  quotas,
  teams,
  locations,
  isUserEndDateEnabled,
  hourlyLeaveAccounting,
  showUpdateUserForm,
  updateWorkingDays,
  updateUserProfile,
  onQuotaUpdate,
  onEdit,
  setActiveTab,
  setShowUpdateUsersForm,
  sendInvite,
}: IUserProfileTab): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { modal } = App.useApp()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { featureFlags } = useAppSelector(selectFeatureFlagsSlice)
  const { managedLocationIds } = useLocationAdmin()
  // A location admin (incl. one who is also an approver) may only EDIT users in their managed locations;
  // approver-only users are view-only. Full admins can edit anyone.
  const canEditUser = amIAdmin && (user.id === authUser.id || !isLocationAdmin || managedLocationIds.includes(user.location.id))

  const [isEditWorkWeekFormVisible, showEditWorkWeekForm] = useState(false)
  const [visibleUserUpdateForm, setVisibleUserUpdateForm] = useState(false)

  useEffect(() => {
    if (typeof showUpdateUserForm === 'boolean') {
      setVisibleUserUpdateForm(showUpdateUserForm)
    }
  }, [showUpdateUserForm])

  const handleWorkingDaysUpdate = async (userWorkHours: WorkingHours | IWorkingSchedule): Promise<void> => {
    if (amIAdmin && updateWorkingDays) {
      await updateWorkingDays(userWorkHours)
      showEditWorkWeekForm(false)
    }
  }

  const handleUserProfileUpdate = async (data) => {
    if (amIAdmin && updateUserProfile) {
      await updateUserProfile(data)
      setVisibleUserUpdateForm(false)
      setShowUpdateUsersForm && setShowUpdateUsersForm(false)
    }
  }

  return (
    <div className="user-profile-page">
      <Card
        title={<IntlMessages id="components.userProfileTab.userProfile" />}
        style={{ marginBottom: 30 }}
        styles={{
          body: {
            padding: 0,
          },
        }}
        extra={
          canEditUser && (
            <Button type="link" size="small" onClick={() => setVisibleUserUpdateForm(true)}>
              <IntlMessages id="app.edit" />
            </Button>
          )
        }
      >
        <div style={{ padding: 25 }}>
          <Row justify="start" gutter={[0, 15]}>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="components.userProfileTab.name" />
                </Text>
                <Text data-testid="user-name">
                  {user.name}{' '}
                  {user.isNameLocked && user.platform !== 'email' ? (
                    <Tooltip className="info-tooltip" title={<IntlMessages id="components.userProfileTab.nameEdited" />}>
                      <LockOutlined />
                    </Tooltip>
                  ) : (
                    ''
                  )}{' '}
                  {user.role.toLowerCase() !== 'admin' && user.id === authUser.id && user.platform === 'email' && (
                    <Button onClick={onEdit}>
                      <IntlMessages id="app.edit" />
                    </Button>
                  )}
                </Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="components.userProfileTab.team" />
                </Text>
                {amIAdmin ? <Link to={`/app/settings/departments/${user.team.id}`}>{user.team.name}</Link> : <Text>{user.team.name}</Text>}
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="components.userProfileTab.location" />
                </Text>
                {amIAdmin ? <Link to={`/app/settings/locations/${user.location.id}/general`}>{user.location.name}</Link> : <Text>{user.location.name}</Text>}
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="app.email" />
                </Text>
                <Text>{user.email && user.email.startsWith('deleted+') ? <IntlMessages id="app.emailDeleted" /> : user.email}</Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="components.userProfileTab.employeeSince" />
                </Text>
                <Text>{user.startDate.split('T')[0]}</Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="components.userProfileTab.role" />
                </Text>
                <Text>
                  {(() => {
                    if (user.isAdmin) return <IntlMessages id="app.role.administrator" />
                    const isApprover = user.role?.toLowerCase() === 'approver'
                    const isLocationAdmin = user.roleAssignments?.some((a) => a.roleId === 'LOCATION_ADMIN')
                    const labels: React.ReactNode[] = []
                    if (isApprover) labels.push(<IntlMessages key="approver" id="app.role.approver" />)
                    if (isLocationAdmin) {
                      if (labels.length > 0) labels.push(<span key="sep">, </span>)
                      labels.push(<IntlMessages key="locAdmin" id="app.role.locationAdmin" />)
                    }
                    if (labels.length > 0) return <>{labels}</>
                    return <IntlMessages id="app.user" />
                  })()}
                  &nbsp;(
                  <a href={getOnboardingLink(user.platform as string, user.role)} target="_blank" rel="noreferrer">
                    <IntlMessages id="components.heatmap.clickForMore" />
                  </a>
                  )
                </Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="components.userProfileTab.status" />
                </Text>
                <Text>
                  {formatMessage({
                    id: user.status === 'ACTIVE' ? 'app.active' : user.status === 'DELETED' ? 'users.deletedUsers' : 'users.inactiveUsers',
                  }).toUpperCase()}
                  {authCompany?.platform === PlatformEnum.Email && user.isGhostUser && (
                    <>
                      &nbsp;
                      {user.userInvitationStatus && (
                        <Tag
                          color={
                            user.userInvitationStatus === EmailInviteStatusEnum.pending
                              ? 'purple'
                              : user.userInvitationStatus === EmailInviteStatusEnum.notInvited
                                ? 'orange'
                                : 'red'
                          }
                        >
                          {user.userInvitationStatus === EmailInviteStatusEnum.pending && <IntlMessages id="users.invitedPending" />}
                          {user.userInvitationStatus === EmailInviteStatusEnum.notInvited && <IntlMessages id="users.uninvited" />}
                          {user.userInvitationStatus === EmailInviteStatusEnum.expired && <IntlMessages id="users.invitedExpired" />}
                        </Tag>
                      )}
                      {!user.userInvitationStatus && <Tag color="orange">{formatMessage({ id: 'users.uninvited' })}</Tag>}
                    </>
                  )}
                  {authCompany?.platform === PlatformEnum.Email && user.isGhostUser && user.userInvitationStatus !== EmailInviteStatusEnum.pending && canEditUser && (
                    <Button
                      onClick={() => {
                        modal.confirm({
                          width: 500,
                          title: formatMessage({ id: 'app.invite' }),
                          content: formatMessage({ id: 'users.inviteUserConfirm' }, { email: user.email }),
                          icon: <MailOutlined />,
                          okText: user.userInvitationStatus === EmailInviteStatusEnum.expired ? formatMessage({ id: 'users.reinvite' }) : formatMessage({ id: 'app.invite' }),
                          onOk() {
                            sendInvite && sendInvite(user.email)
                          },
                        })
                      }}
                    >
                      {user.userInvitationStatus === EmailInviteStatusEnum.expired ? <IntlMessages id="users.reinvite" /> : <IntlMessages id="app.invite" />}
                    </Button>
                  )}
                </Text>
              </Space>
            </Col>
            {user.endDate && (
              <Col span={8}>
                <Space direction="vertical" size={[1, -15]} wrap>
                  <Text strong>
                    <IntlMessages id="components.userProfileTab.endDate" />
                  </Text>
                  <Text>{user.endDate.split('T')[0]}</Text>
                </Space>
              </Col>
            )}
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Space direction="horizontal">
                  <Text strong>
                    <IntlMessages id="user.employeeId" />
                  </Text>
                  <Tooltip className="info-tooltip" title={<IntlMessages id="components.userProfileTab.employeeIdTooltip" />}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <Text>{user.employeeId ? user.employeeId : <IntlMessages id="components.userProfileTab.employeeIdNotSet" />}</Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space direction="vertical" size={[1, -15]} wrap>
                <Text strong>
                  <IntlMessages id="app.role.approvers" />:
                </Text>
                <Paragraph>{user.approvers.join(', ')}</Paragraph>
              </Space>
            </Col>
            {user.birthday && (
              <Col span={8}>
                <Space direction="vertical" size={[1, -15]} wrap>
                  <Text strong>
                    <IntlMessages id="user.birthday" />
                  </Text>
                  <Text>{user.birthday}</Text>
                </Space>
              </Col>
            )}
          </Row>
        </div>
      </Card>

      <Card
        title={[
          <span key="title">{formatMessage({ id: 'components.userProfileTab.workingDays' })} </span>,
          user.workWeekType === 'USER' && (
            <Tooltip
              key="info"
              title={
                user.workWeekInfo ? (
                  <IntlMessages
                    id="components.userProfileTab.customWorkWeekTooltip"
                    values={{
                      userName: user.name,
                      timestamp: format(new Date(user.workWeekInfo[0]), 'MMMM d, yyyy'),
                      adminName: user.workWeekInfo[1],
                    }}
                  />
                ) : (
                  <IntlMessages id="components.userProfileTab.customWorkWeekEditedBefore" values={{ userName: user.name }} />
                )
              }
            >
              <Tag color="purple">
                <IntlMessages id="components.userProfileTab.customWorkWeekLabel" />
              </Tag>
            </Tooltip>
          ),
        ]}
        style={{ marginBottom: 30 }}
        styles={{
          body: { padding: 0 },
        }}
        extra={
          canEditUser && (
            <Button type="link" size="small" onClick={() => showEditWorkWeekForm(true)}>
              <IntlMessages id="app.edit" />
            </Button>
          )
        }
      >
        <UserWorkingDaysAndHours firstDayOfWeek={firstDayOfWeek} workingHours={workingHours} />
      </Card>
      <UserLeaveQuotas
        quotas={quotas}
        userId={user.id}
        amIAdmin={canEditUser}
        IntlMessages={IntlMessages}
        onQuotaUpdate={onQuotaUpdate}
        location={user.location}
        userName={user.name}
        hourlyLeaveAccounting={hourlyLeaveAccounting}
        setActiveTab={setActiveTab}
        locale={user.locale || LocaleEnum.en}
        userStartDate={user.startDate}
        userStatus={user.status}
      />

      {canEditUser && isEditWorkWeekFormVisible && (
        <UpdateWorkweekForm
          userWorkHours={workingHours}
          firstDayOfTheWeek={firstDayOfWeek}
          handleSave={(userWorkHours: WorkingHours | IWorkingSchedule) => {
            handleWorkingDaysUpdate(userWorkHours)
          }}
          company={authCompany}
          hourlyLeaveAccounting={hourlyLeaveAccounting}
          featureFlags={featureFlags}
          handleCancel={() => showEditWorkWeekForm(false)}
          visible={isEditWorkWeekFormVisible}
        />
      )}
      {canEditUser && teams && visibleUserUpdateForm && (
        <UserUpdateForm
          initialRolloverChanged={Boolean(quotas[0].leaveTypes.find((lt) => lt.rolloverFromPreviousYear !== 0))}
          visible={visibleUserUpdateForm}
          user={user}
          roleAssignments={user.roleAssignments}
          teams={teams}
          locations={locations}
          isLocationAdmin={isLocationAdmin}
          handleCancel={() => {
            setVisibleUserUpdateForm(false)
            setShowUpdateUsersForm && setShowUpdateUsersForm(false)
          }}
          onSave={(data) => {
            handleUserProfileUpdate(data)
          }}
          enableUserEndDate={isUserEndDateEnabled}
          companyPlan={authCompany?.plan as SubscriptionPlanEnum}
        />
      )}
    </div>
  )
}

export default UserProfileTab
